'use client';

import type { AssistantState, ToolCallMessagePart } from '@assistant-ui/react';
import { useAuiState } from '@assistant-ui/react';
import type { SearchResult } from 'conversational-search-client';

type ThreadMessages = AssistantState['thread']['messages'];
type MessagePart = ThreadMessages[number]['parts'][number];
type ToolCallPart = ToolCallMessagePart & { status: MessagePart['status'] };

interface SearchResultsState {
    results: SearchResult[];
    totalCount: number;
    isSearching: boolean;
    hasSearched: boolean;
}

interface ToolCallData {
    result: SearchResult[];
    total?: number;
}

function isToolCall(part: MessagePart): part is ToolCallPart {
    return part.type === 'tool-call';
}

function parseSearchData(toolCall: ToolCallPart): ToolCallData | null {
    if (!toolCall.result || typeof toolCall.result !== 'object') return null;
    const { data } = toolCall.result as { data?: ToolCallData };
    return data?.result?.length ? data : null;
}

function extractSearchResults(messages: ThreadMessages, threadIsRunning: boolean): SearchResultsState {
    const lastAssistant = messages.findLast((m) => m.role === 'assistant' && m.parts.some(isToolCall));
    const toolCalls = lastAssistant?.parts.filter(isToolCall) ?? [];
    const parsed = toolCalls.flatMap((tc) => {
        const data = parseSearchData(tc);
        return data ? [{ data, running: tc.status.type === 'running' }] : [];
    });

    if (parsed.length === 0) {
        return {
            results: [],
            totalCount: 0,
            isSearching: threadIsRunning,
            hasSearched: false,
        };
    }

    const allResults = parsed.toReversed().flatMap((p) => p.data.result);
    const uniqueResults = [...new Map<number, SearchResult>(allResults.map((r) => [r.contentId, r])).values()];

    return {
        results: uniqueResults,
        totalCount: uniqueResults.length,
        isSearching: threadIsRunning,
        hasSearched: true,
    };
}

/**
 * Extract the latest search results from the thread's tool-call parts.
 */
export function useSearchResults(): SearchResultsState {
    const messages = useAuiState(({ thread }) => thread.messages);
    const threadIsRunning = useAuiState(({ thread }) => thread.isRunning);
    return extractSearchResults(messages, threadIsRunning);
}
